import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { AllianceListEntry, MyAllianceResponse } from "../types/alliance";

export const allianceKeys = {
  mine: ["alliance", "mine"] as const,
  list: ["alliance", "list"] as const,
  research: ["alliance", "research"] as const,
  contributors: ["alliance", "contributors"] as const,
  buildings: ["alliance", "buildings"] as const,
};

export interface AllianceBuildingView {
  id: string;
  name: string;
  description: string;
  effect: string;
  currentLevel: number;
  maxLevel: number;
  currentEffectPct: number;
  nextLevel: number;
  nextEffectStepPct: number;
  nextCost: { gold: number; wood: number; stone: number; mana: number } | null;
  nextDurationSeconds: number | null;
}

export interface AllianceBuildingActive {
  buildingId: string;
  targetLevel: number;
  startedAt: string;
  finishesAt: string;
  startedBy: string;
}

export interface AllianceBuildingsResponse {
  allianceId: string;
  buildings: AllianceBuildingView[];
  active: AllianceBuildingActive | null;
  treasury: { gold: number; wood: number; stone: number; mana: number };
}

// Mirrors AllianceResearchEndpoints.cs /api/alliance/research response.
// Each tree carries cumulative state plus a peek at the next rank so the
// UI can render "current effect / next step" without re-deriving costs.
export type AllianceResearchSection = "Development" | "Economy" | "Military";

export interface AllianceResearchTreeView {
  id: string;
  section: AllianceResearchSection;
  name: string;
  description: string;
  effect: string;
  magnitude: string;
  currentRank: number;
  maxRank: number;
  currentEffectPct: number;
  nextRank: number;
  nextEffectStepPct: number;
  nextCost: { gold: number; wood: number; stone: number; mana: number } | null;
  nextDurationSeconds: number | null;
}

export interface AllianceResearchActive {
  treeId: string;
  targetRank: number;
  startedAt: string;
  finishesAt: string;
  startedBy: string;
}

export interface AllianceResearchResponse {
  allianceId: string;
  trees: AllianceResearchTreeView[];
  active: AllianceResearchActive | null;
  treasury: { gold: number; wood: number; stone: number; mana: number };
}

export interface AllianceContributor {
  playerId: string;
  name: string;
  total: number;
}

export function useMyAlliance() {
  return useQuery({
    queryKey: allianceKeys.mine,
    queryFn: async () => {
      const res = await api.get<MyAllianceResponse>("/alliances/mine");
      return res.data;
    },
    refetchInterval: 15_000,
  });
}

export function useAlliances(enabled: boolean) {
  return useQuery({
    queryKey: allianceKeys.list,
    queryFn: async () => {
      const res = await api.get<AllianceListEntry[]>("/alliances");
      return res.data;
    },
    enabled,
    staleTime: 15_000,
  });
}

function invalidateBoth(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: allianceKeys.mine });
  qc.invalidateQueries({ queryKey: allianceKeys.list });
}

export function useCreateAlliance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { tag: string; name: string; description: string }) => {
      const res = await api.post("/alliances", body);
      return res.data;
    },
    onSuccess: () => invalidateBoth(qc),
  });
}

export function useJoinAlliance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post(`/alliances/${id}/join`);
      return res.data;
    },
    onSuccess: () => invalidateBoth(qc),
  });
}

export function useLeaveAlliance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await api.post("/alliances/leave");
      return res.data;
    },
    onSuccess: () => invalidateBoth(qc),
  });
}

export function useKickFromAlliance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ allianceId, playerId }: { allianceId: string; playerId: string }) => {
      const res = await api.post(`/alliances/${allianceId}/kick/${playerId}`);
      return res.data;
    },
    onSuccess: () => invalidateBoth(qc),
  });
}

export function useDisbandAlliance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (allianceId: string) => {
      const res = await api.post(`/alliances/${allianceId}/disband`);
      return res.data;
    },
    onSuccess: () => invalidateBoth(qc),
  });
}

// Alliance research / treasury / contributions (D-45).

export function useAllianceResearch(enabled: boolean) {
  return useQuery({
    queryKey: allianceKeys.research,
    queryFn: async () => {
      const res = await api.get<AllianceResearchResponse>("/alliance/research");
      return res.data;
    },
    enabled,
    refetchInterval: 10_000,
  });
}

export function useStartAllianceResearch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (treeId: string) => {
      const res = await api.post("/alliance/research/start", { treeId });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: allianceKeys.research });
    },
  });
}

export function useContributeToAlliance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      gold: number;
      wood: number;
      stone: number;
      mana: number;
    }) => {
      const res = await api.post("/alliance/contribute", body);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: allianceKeys.research });
      qc.invalidateQueries({ queryKey: allianceKeys.contributors });
      qc.invalidateQueries({ queryKey: ["castle"] });
    },
  });
}

export function useAllianceBuildings(enabled: boolean) {
  return useQuery({
    queryKey: allianceKeys.buildings,
    queryFn: async () => {
      const res = await api.get<AllianceBuildingsResponse>("/alliance/buildings");
      return res.data;
    },
    enabled,
    refetchInterval: 10_000,
  });
}

export function useStartAllianceBuilding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (buildingId: string) => {
      const res = await api.post("/alliance/buildings/start", { buildingId });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: allianceKeys.buildings });
      qc.invalidateQueries({ queryKey: allianceKeys.research });
    },
  });
}

export function useAllianceContributors(enabled: boolean) {
  return useQuery({
    queryKey: allianceKeys.contributors,
    queryFn: async () => {
      const res = await api.get<AllianceContributor[]>("/alliance/contributors");
      return res.data;
    },
    enabled,
    refetchInterval: 30_000,
  });
}
